import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Field, useForm, useFormState } from 'react-final-form';
import { useNavigate } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import { ThreeDots } from 'react-loader-spinner';

import { getAccessAssistants, getAccessUsers, loadMoreAccessUsers } from '../../../redux/projects/projectsActions';
import CommonButton from '../../../components/common-button/CommonButton';
import AccessContainers from '../../../components/access-containers/AccessContainers';
import WithError from '../../../components/with-error/WithError';
import SearchInput from '../../../components/common-inputs/SearchInput';
import WithLabel, { LABEL_STYLES } from '../../../components/with-label/WithLabel';
import MultiselectDropdownNew from '../../../components/multiselect-dropdown/MultiselectDropdownNew';
import { ASSISTANTS_CATEGORY } from '../../../constants';
import { Input } from '../../../components/common-inputs';
import CommonTextarea from '../../../components/common-textarea/CommonTextarea';

import scss from './GenieGoCreation.module.scss';

function GenieGoCreationForm({ handleSubmit }) {
  const { t } = useTranslation('projects');
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { restart, change } = useForm();
  const { values } = useFormState();

  const isAccessUsersLoading = useSelector((state) => state.projects.isAccessUsersLoading);
  const isLoading = useSelector((state) => state.projects.isLoading);
  const { accessUsers, totalAccessUsers, accessAssistants } = useSelector((state) => state.projects);

  const inactiveUsers = useMemo(
    () => accessUsers.filter((user) => !values.activeUsers.some((activeUser) => activeUser.id === user.id)),
    [values, accessUsers],
  );

  const handleRestForm = () => {
    restart();
    navigate('/projects/create');
  };

  const handleActivateUser = (user) => {
    change('activeUsers', [...values.activeUsers, user]);
  };

  const handleInactivateUser = ({ id }) => {
    change(
      'activeUsers',
      values.activeUsers.filter((user) => user.id !== id),
    );
  };

  const handleSearchUsers = (newSearch) => {
    change('assignUser', newSearch);
    dispatch(getAccessUsers({ search: newSearch, page: 1 }));
  };

  const handleLoadMoreUsers = (newPage) => {
    dispatch(loadMoreAccessUsers({ search: values.assignUser ?? '', page: newPage }));
  };

  useEffect(() => {
    dispatch(getAccessUsers({ search: '', page: 1 }));
    dispatch(getAccessAssistants());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <form className={scss.form} onSubmit={handleSubmit}>
      <Field name='projectName' autoComplete='project-name'>
        {({ meta, input }) => (
          <WithLabel variant={LABEL_STYLES.CUSTOM} label={`${t('projectName')}:`} isBold extraClassName={scss.inputRow}>
            <WithError isShowError={meta.touched && meta.error} errorText={t(meta.error)} isAbsolute>
              <Input
                type='text'
                size='sm'
                isError={meta.touched && meta.error}
                formProps={input}
                disabled={isLoading}
                autoComplete='project-name'
              />
            </WithError>
          </WithLabel>
        )}
      </Field>

      <Field name='brand' autoComplete='brand'>
        {({ meta, input }) => (
          <WithLabel variant={LABEL_STYLES.CUSTOM} label={`${t('brand')}:`} isBold extraClassName={scss.inputRow}>
            <WithError isShowError={meta.touched && meta.error} errorText={t(meta.error)} isAbsolute>
              <Input
                type='text'
                size='sm'
                isError={meta.touched && meta.error}
                formProps={input}
                disabled={isLoading}
                autoComplete='brand'
              />
            </WithError>
          </WithLabel>
        )}
      </Field>

      <Field name='activeAllAssistants'>
        {({ input, meta }) => (
          <WithLabel variant={LABEL_STYLES.CUSTOM} label={`${t('assistants')}:`} isBold extraClassName={scss.inputRow}>
            <WithError isShowError={meta.touched && meta.error} errorText={t(meta.error)} isAbsolute>
              <MultiselectDropdownNew
                value={input.value}
                options={accessAssistants[ASSISTANTS_CATEGORY.GENERATE_ALL]}
                onChange={input.onChange}
                name={input.name}
                onBlur={input.onBlur}
                onFocus={input.onFocus}
                isError={meta.touched && meta.error}
                placeholder={t('selectAssistants')}
                disableNewOptions
              />
            </WithError>
          </WithLabel>
        )}
      </Field>

      <Field name='description' autoComplete='genie-go-description'>
        {({ meta, input }) => (
          <WithLabel
            variant={LABEL_STYLES.CUSTOM}
            label={`${t('description')}:`}
            isBold
            extraClassName={scss.inputRowLong}>
            <WithError isShowError={meta.touched && meta.error} errorText={t(meta.error)} isAbsolute>
              <CommonTextarea
                name={input.name}
                value={input.value}
                onChange={input.onChange}
                onBlur={input.onBlur}
                onFocus={input.onFocus}
                autoComplete={input.autoComplete}
                isError={meta.touched && meta.error}
                disabled={isLoading}
                rows='3'
                resize='none'
              />
            </WithError>
          </WithLabel>
        )}
      </Field>

      <Field name='referenceMessages' autoComplete='genie-go-reference-messages'>
        {({ meta, input }) => (
          <WithLabel
            variant={LABEL_STYLES.CUSTOM}
            label={`${t('referenceMessages')}:`}
            isBold
            extraClassName={scss.inputRowLong}>
            <WithError isShowError={meta.touched && meta.error} errorText={t(meta.error)} isAbsolute>
              <CommonTextarea
                name={input.name}
                value={input.value}
                onChange={input.onChange}
                onBlur={input.onBlur}
                onFocus={input.onFocus}
                autoComplete={input.autoComplete}
                isError={meta.touched && meta.error}
                disabled={isLoading}
                rows='5'
              />
            </WithError>
          </WithLabel>
        )}
      </Field>

      <Field name='assignUser'>
        {({ input }) => (
          <WithLabel label={t('assignUser')} variant={LABEL_STYLES.CUSTOM} isBold extraClassName={scss.inputRow}>
            <SearchInput
              value={input.value}
              onChange={handleSearchUsers}
              name={input.name}
              size='sm'
              disabled={isLoading}
            />
          </WithLabel>
        )}
      </Field>

      <Field name='activeUsers'>
        {({ input, meta }) => (
          <WithError isShowError={meta.touched && meta.error} errorText={t(meta.error)}>
            <AccessContainers
              onInactive={handleInactivateUser}
              onActive={handleActivateUser}
              inactive={inactiveUsers}
              active={input.value}
              disabled={isLoading}
              isLoading={isAccessUsersLoading}
              searchStr={values.assignUser}
              onLoadMore={handleLoadMoreUsers}
              totalElements={totalAccessUsers}
              inactiveLoadedSize={accessUsers.length}
            />
          </WithError>
        )}
      </Field>

      <div className={scss.actions}>
        <CommonButton type='submit' styleColor='primary' onClick={handleSubmit} maxWidth={100} disabled={isLoading}>
          {isLoading ? <ThreeDots height={15} width={40} /> : null}
          {t('create')}
        </CommonButton>
        <CommonButton type='button' styleColor='secondary' onClick={handleRestForm} maxWidth={100} disabled={isLoading}>
          {t('cancel')}
        </CommonButton>
      </div>
    </form>
  );
}

export default GenieGoCreationForm;
